import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { reconciliationService } from '../../services/reconciliationService';
import { transactionService } from '../../services/transactionService';
import TransactionModal from './TransactionModal';
import ReconciliationMatchModal from './ReconciliationMatchModal';
import ReconciliationSuccessModal from './ReconciliationSuccessModal';
import {
  calculateReconciliationSummary,
  getAccountDirection
} from '../../utils/reconciliationLogic';

const {
  FiArrowLeft,
  FiArrowLeftRight,
  FiCalendar,
  FiCheckCircle,
  FiInfo,
  FiAlertCircle,
  FiDollarSign,
  FiCreditCard,
  FiHelpCircle,
  FiEdit2,
  FiTrash2,
  FiX,
  FiGitMerge
} = FiIcons;

/**
 * Format a monetary value using the organization's currency.
 */
const formatMoney = (amount, currency = 'USD') => {
  const value = Number(amount) || 0;

  let formatted;

  try {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    }).format(Math.abs(value));
  } catch {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(Math.abs(value));
  }

  return value < 0 ? `-${formatted}` : formatted;
};

/**
 * Get a simple currency display symbol/code for the input prefix.
 *
 * We intentionally keep the full currency formatting inside formatMoney().
 * This helper is only for the closing-balance input prefix.
 */
const getCurrencySymbol = (currency = 'USD') => {
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      currencyDisplay: 'symbol'
    }).formatToParts(0);

    return (
      parts.find((part) => part.type === 'currency')?.value
      || currency
      || 'USD'
    );
  } catch {
    return currency || 'USD';
  }
};

const formatDate = (date) => {
  if (!date) return '—';

  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const ActionButton = ({
  label,
  icon,
  onClick,
  className,
  disabled = false
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`group relative inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
  >
    <SafeIcon icon={icon} className="text-base" />

    <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-bold text-white shadow-lg group-hover:block group-focus-visible:block">
      {label}
    </span>
  </button>
);

export default function TransactionReconciliationPanel({
  organizationId,
  organizationCurrency = 'USD',
  accounts,
  groupedAccounts,
  entries,
  selectedAccountId,
  initialManualReconciliation,
  onAccountChange,
  onCompleted,
  onManualReconciliationComplete,
  onBackToHistory,
  onViewHistory
}) {
  const [accountId, setAccountId] = useState('');
  const [statementDate, setStatementDate] = useState('');
  const [endingBalance, setEndingBalance] = useState('');
  const [activeReconciliationId, setActiveReconciliationId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [completedAccount, setCompletedAccount] = useState(null);
  const [isManualMode, setIsManualMode] = useState(false);
  const [matchedTransactionIds, setMatchedTransactionIds] = useState([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deletingTransaction, setDeletingTransaction] = useState(null);

  const currency = organizationCurrency || 'USD';
  const currencySymbol = useMemo(
    () => getCurrencySymbol(currency),
    [currency]
  );

  const isHistoryManual = Boolean(
    initialManualReconciliation?.reconciliationId
  );

  /**
   * Reset panel state whenever the active organization/account/history
   * context changes.
   *
   * This is important for multi-tenant isolation: switching organizations
   * must not leave a reconciliation from the previous organization active.
   */
  useEffect(() => {
    const initialAccountId = initialManualReconciliation?.accountId;

    const hasInitialAccount = accounts.some(
      (account) => account.id === initialAccountId
    );

    const hasSelectedAccount = accounts.some(
      (account) => account.id === selectedAccountId
    );

    const nextAccountId = hasInitialAccount
      ? initialAccountId
      : hasSelectedAccount
        ? selectedAccountId
        : '';

    setAccountId(nextAccountId);

    setStatementDate(
      hasInitialAccount
        ? initialManualReconciliation.statementDate
        : ''
    );

    setEndingBalance(
      hasInitialAccount
        ? String(initialManualReconciliation.endingBalance)
        : ''
    );

    setActiveReconciliationId(
      hasInitialAccount
        ? initialManualReconciliation.reconciliationId || null
        : null
    );

    setError('');
    setIsMatchModalOpen(false);
    setIsManualMode(hasInitialAccount);
    setMatchedTransactionIds([]);
    setSelectedTransactionIds([]);
    setEditingTransaction(null);
    setDeletingTransaction(null);
    setCompletedAccount(null);
  }, [
    accounts,
    initialManualReconciliation,
    selectedAccountId,
    organizationId
  ]);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === accountId),
    [accounts, accountId]
  );

  /**
   * Calculate the current balance and reconciliation count for
   * each available accounting account.
   */
  const accountDetails = useMemo(
    () => (
      accounts.reduce((details, account) => {
        const accountEntries = entries.filter(
          (entry) => entry.coa_id === account.id
        );

        const direction = getAccountDirection(account);

        const balance = direction * accountEntries.reduce(
          (sum, entry) => (
            sum
            + (Number(entry.debit) || 0)
            - (Number(entry.credit) || 0)
          ),
          0
        );

        const reconciledCount = accountEntries.filter(
          (entry) => entry.status === 'Reconciled'
        ).length;

        details[account.id] = {
          balance,
          reconciledCount
        };

        return details;
      }, {})
    ),
    [accounts, entries]
  );

  const summary = useMemo(
    () => calculateReconciliationSummary(
      entries,
      selectedAccount,
      statementDate,
      endingBalance
    ),
    [
      entries,
      selectedAccount,
      statementDate,
      endingBalance
    ]
  );

  /**
   * Transactions available for manual reconciliation.
   *
   * These are accounting.transactions entries associated with
   * the selected accounting/COA account.
   *
   * Import source account is NOT used here as the accounting account.
   */
  const manualTransactions = useMemo(() => {
    if (!selectedAccount || !statementDate) {
      return [];
    }

    const cutoff = new Date(
      `${statementDate}T23:59:59`
    ).getTime();

    const direction = getAccountDirection(selectedAccount);

    const eligibleEntries = entries
      .filter((entry) => (
        entry.coa_id === selectedAccount.id
        && entry.status !== 'Reconciled'
        && new Date(
          `${entry.date}T00:00:00`
        ).getTime() <= cutoff
      ))
      .sort((first, second) => {
        const dateDifference =
          new Date(`${first.date}T00:00:00`)
          - new Date(`${second.date}T00:00:00`);

        if (dateDifference !== 0) {
          return dateDifference;
        }

        return new Date(first.created_at || 0)
          - new Date(second.created_at || 0);
      });

    let runningBalance = summary.startingBalance;

    return eligibleEntries
      .map((entry) => {
        const rawAmount =
          (Number(entry.debit) || 0)
          - (Number(entry.credit) || 0);

        const amount = rawAmount * direction;

        runningBalance += amount;

        return {
          ...entry,
          amount,
          runningBalance
        };
      })
      .reverse();
  }, [
    entries,
    selectedAccount,
    statementDate,
    summary.startingBalance
  ]);

  /**
   * Remove transaction selections that no longer exist.
   */
  useEffect(() => {
    const availableTransactionIds = new Set(
      manualTransactions.map(
        (transaction) => transaction.id
      )
    );

    setMatchedTransactionIds((currentIds) => (
      currentIds.filter(
        (transactionId) =>
          availableTransactionIds.has(transactionId)
      )
    ));

    setSelectedTransactionIds((currentIds) => (
      currentIds.filter(
        (transactionId) =>
          availableTransactionIds.has(transactionId)
      )
    ));
  }, [manualTransactions]);

  const matchedTransactions = useMemo(
    () => (
      manualTransactions.filter(
        (transaction) =>
          matchedTransactionIds.includes(transaction.id)
      )
    ),
    [manualTransactions, matchedTransactionIds]
  );

  const selectedManualTransactions = useMemo(
    () => (
      manualTransactions.filter(
        (transaction) =>
          selectedTransactionIds.includes(transaction.id)
      )
    ),
    [manualTransactions, selectedTransactionIds]
  );

  const matchedBalance = useMemo(
    () => (
      summary.startingBalance
      + matchedTransactions.reduce(
        (total, transaction) =>
          total + transaction.amount,
        0
      )
    ),
    [matchedTransactions, summary.startingBalance]
  );

  const manualDifference = useMemo(
    () => (
      (Number(endingBalance) || 0)
      - matchedBalance
    ),
    [endingBalance, matchedBalance]
  );

  const isManualBalanced = Boolean(
    statementDate
    && endingBalance !== ''
    && Math.abs(manualDifference) < 0.01
  );

  const hasRequiredDetails = Boolean(
    organizationId
    && selectedAccount
    && statementDate
    && endingBalance !== ''
  );

  const canReconcile =
    hasRequiredDetails
    && summary.isBalanced;

  const unmatchedCount =
    manualTransactions.length
    - matchedTransactionIds.length;

  const allManualTransactionsSelected = (
    manualTransactions.length > 0
    && selectedTransactionIds.length
      === manualTransactions.length
  );

  const chooseAccount = (nextAccountId) => {
    setAccountId(nextAccountId);
    setStatementDate('');
    setEndingBalance('');
    setActiveReconciliationId(null);
    setError('');
    setIsManualMode(false);
    setMatchedTransactionIds([]);
    setSelectedTransactionIds([]);

    onAccountChange(nextAccountId);
  };

  /**
   * Start reconciliation.
   *
   * If the books do not match the statement, an unfinished reconciliation
   * record is created and the user enters manual reconciliation mode.
   */
  const handleStart = async (event) => {
    event.preventDefault();

    if (!organizationId) {
      setError(
        'No active organization is selected.'
      );
      return;
    }

    if (!hasRequiredDetails) {
      setError(
        'Enter the statement end date and closing balance to continue.'
      );
      return;
    }

    setError('');

    if (!summary.isBalanced) {
      setIsSaving(true);

      try {
        const reconciliationId =
          await reconciliationService
            .recordUnbalancedLedgerReconciliation({
              organizationId,
              coaId: selectedAccount.id,
              statementDate,
              endingBalance
            });

        setActiveReconciliationId(
          reconciliationId
        );

        setMatchedTransactionIds([]);
        setSelectedTransactionIds([]);
        setIsManualMode(true);
      } catch (err) {
        setError(
          err.message
          || 'Unable to record the unbalanced reconciliation.'
        );
      } finally {
        setIsSaving(false);
      }

      return;
    }

    setIsMatchModalOpen(true);
  };

  /**
   * Complete automatic reconciliation.
   */
  const handleAutomaticReconciliation = async () => {
    if (!organizationId || !canReconcile) {
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await reconciliationService.completeLedgerReconciliation({
        organizationId,
        coaId: selectedAccount.id,
        statementDate,
        endingBalance
      });

      await onCompleted?.(selectedAccount);

      setIsMatchModalOpen(false);
      setCompletedAccount(selectedAccount);
    } catch (err) {
      setIsMatchModalOpen(false);

      setError(
        err.message
        || 'Unable to complete reconciliation.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualReconciliation = () => {
    setIsMatchModalOpen(false);
    setMatchedTransactionIds([]);
    setSelectedTransactionIds([]);
    setIsManualMode(true);
  };

  const handleMatchTransaction = (transactionId) => {
    setError('');

    setMatchedTransactionIds((currentIds) => (
      currentIds.includes(transactionId)
        ? currentIds.filter(
          (id) => id !== transactionId
        )
        : [...currentIds, transactionId]
    ));
  };

  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactionIds((currentIds) => (
      currentIds.includes(transactionId)
        ? currentIds.filter(
          (id) => id !== transactionId
        )
        : [...currentIds, transactionId]
    ));
  };

  const handleSelectAllTransactions = (event) => {
    setSelectedTransactionIds(
      event.target.checked
        ? manualTransactions.map(
          (transaction) => transaction.id
        )
        : []
    );
  };

  const handleMatchSelectedTransactions = () => {
    if (selectedTransactionIds.length === 0) {
      return;
    }

    setError('');

    setMatchedTransactionIds((currentIds) => (
      [
        ...new Set([
          ...currentIds,
          ...selectedTransactionIds
        ])
      ]
    ));

    setSelectedTransactionIds([]);
  };

  const handleDeleteSelectedTransactions = () => {
    if (selectedManualTransactions.length === 0) {
      return;
    }

    setDeletingTransaction(
      selectedManualTransactions
    );
  };

  /**
   * Complete manual reconciliation.
   */
  const handleCompleteManualReconciliation = async () => {
    if (
      !organizationId
      || !isManualBalanced
      || !selectedAccount
    ) {
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      if (activeReconciliationId) {
        await reconciliationService
          .resolveUnbalancedLedgerReconciliation({
            organizationId,
            reconciliationId: activeReconciliationId,
            transactionIds: matchedTransactionIds
          });
      } else {
        await reconciliationService
          .completeSelectedLedgerReconciliation({
            organizationId,
            coaId: selectedAccount.id,
            statementDate,
            endingBalance,
            transactionIds: matchedTransactionIds
          });
      }

      await onCompleted?.(selectedAccount);

      setIsManualMode(false);
      setMatchedTransactionIds([]);
      setSelectedTransactionIds([]);
      setActiveReconciliationId(null);

      if (isHistoryManual) {
        onManualReconciliationComplete?.();
        return;
      }

      setCompletedAccount(selectedAccount);
    } catch (err) {
      setError(
        err.message
        || 'Unable to complete manual reconciliation.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Delete transaction(s).
   *
   * The transaction service remains responsible for the actual
   * transaction deletion and its balancing ledger behavior.
   */
  const handleDeleteTransaction = async () => {
    if (!deletingTransaction) {
      return;
    }

    const transactionsToDelete =
      Array.isArray(deletingTransaction)
        ? deletingTransaction
        : [deletingTransaction];

    setIsSaving(true);
    setError('');

    try {
      const transactionKeys =
        transactionsToDelete.map(
          (transaction) =>
            transaction.batch_id || transaction.id
        );

      if (transactionKeys.length === 1) {
        await transactionService.deleteTransaction(
          transactionKeys[0]
        );
      } else {
        await transactionService.bulkDeleteTransactions(
          transactionKeys
        );
      }

      const deletedIds = new Set(
        transactionsToDelete.map(
          (transaction) => transaction.id
        )
      );

      setMatchedTransactionIds((currentIds) => (
        currentIds.filter(
          (id) => !deletedIds.has(id)
        )
      ));

      setSelectedTransactionIds([]);

      await onCompleted?.(selectedAccount);

      setDeletingTransaction(null);
    } catch (err) {
      setError(
        err.message
        || 'Unable to delete the selected transaction.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * No account selected.
   */
  if (!selectedAccount) {
    return (
      <section className="overflow-hidden rounded-2xl border border-blue-700 bg-white shadow-sm">
        <header className="flex flex-col gap-3 border-b border-slate-200 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-black tracking-tight text-slate-900">
              Choose an account to reconcile
            </h2>

            <SafeIcon
              icon={FiHelpCircle}
              className="text-xl text-blue-600"
            />
          </div>

          <p className="text-sm font-bold text-slate-500">
            Balances as of today
          </p>
        </header>

        {accounts.length === 0 ? (
          <div className="m-5 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
            <SafeIcon
              icon={FiAlertCircle}
              className="mt-0.5 shrink-0"
            />

            Add a Cash and Bank or Credit Card account in the Chart of Accounts before
            starting reconciliation.
          </div>
        ) : (
          <div>
            {Object.entries(groupedAccounts).map(
              ([group, groupAccounts]) => (
                <div key={group}>
                  <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                    <h3 className="text-sm font-black text-slate-900">
                      {group}
                    </h3>
                  </div>

                  {groupAccounts.map((account) => {
                    const details =
                      accountDetails[account.id]
                      || {
                        balance: 0,
                        reconciledCount: 0
                      };

                    const isCreditCard =
                      account.type === 'Liability';

                    return (
                      <button
                        key={account.id}
                        type="button"
                        onClick={() =>
                          chooseAccount(account.id)
                        }
                        className="group flex w-full items-center gap-4 border-b border-slate-200 px-5 py-5 text-left transition-colors last:border-b-0 hover:bg-blue-50/50 focus:bg-blue-50/50 focus:outline-none"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-slate-500 text-slate-600 transition-colors group-hover:border-blue-600 group-hover:text-blue-600">
                          <SafeIcon
                            icon={
                              isCreditCard
                                ? FiCreditCard
                                : FiDollarSign
                            }
                            className="text-xl"
                          />
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-base font-bold text-slate-900">
                            {account.name}
                          </span>

                          <span className="mt-1 block text-xs font-semibold text-slate-500">
                            {details.reconciledCount > 0
                              ? 'Previously reconciled'
                              : 'Never reconciled'}
                          </span>
                        </span>

                        <span className="text-right">
                          <span className="block whitespace-nowrap text-base font-black text-slate-900">
                            {formatMoney(
                              details.balance,
                              currency
                            )}
                          </span>

                          <span
                            className={`mt-1 inline-block rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
                              details.reconciledCount > 0
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {details.reconciledCount > 0
                              ? 'Ready to reconcile'
                              : 'Never reconciled'}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}
      </section>
    );
  }

  return (
    <>
      {isHistoryManual && (
        <button
          type="button"
          onClick={onBackToHistory}
          className="inline-flex items-center gap-2 rounded-full border border-blue-600 px-4 py-2 text-sm font-bold text-blue-700 transition-colors hover:bg-blue-50"
        >
          <SafeIcon icon={FiArrowLeft} />
          Back to reconciliation history
        </button>
      )}

      {!isHistoryManual && (
        <section className="overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm">
          <div className="flex flex-col gap-5 border-b border-blue-100 bg-gradient-to-r from-blue-50 via-white to-white px-5 py-5 md:flex-row md:items-start">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-200">
              <SafeIcon
                icon={FiArrowLeftRight}
                className="text-xl"
              />
            </div>

            <div>
              <h2 className="text-lg font-black tracking-tight text-slate-900">
                Get your statement and start reconciling
              </h2>

              <p className="mt-1 max-w-3xl text-sm font-medium leading-relaxed text-slate-600">
                Confirm the statement end date and closing balance for {selectedAccount.name}.
                We’ll verify that your books match before reconciliation begins.
              </p>
            </div>
          </div>

          <form
            onSubmit={handleStart}
            className="p-5 md:p-6"
          >
            {error && (
              <div className="mb-5 flex gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-700">
                <SafeIcon
                  icon={FiAlertCircle}
                  className="mt-0.5 shrink-0"
                />

                {error}
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-3 md:items-end">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                  Starting balance
                </p>

                <p className="mt-3 text-xl font-black text-slate-900">
                  {formatMoney(
                    summary.startingBalance,
                    currency
                  )}
                </p>

                <p className="mt-1 text-[11px] font-bold text-emerald-600">
                  Previously reconciled
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                  Statement end date
                </label>

                <div className="relative">
                  <SafeIcon
                    icon={FiCalendar}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="date"
                    value={statementDate}
                    onChange={(event) => {
                      setStatementDate(event.target.value);
                      setError('');
                      setActiveReconciliationId(null);
                      setIsManualMode(false);
                      setMatchedTransactionIds([]);
                      setSelectedTransactionIds([]);
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm font-bold text-slate-900 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                  Closing balance
                </label>

                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-black text-slate-400">
                    {currencySymbol}
                  </span>

                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={endingBalance}
                    onChange={(event) => {
                      setEndingBalance(event.target.value);
                      setError('');
                      setActiveReconciliationId(null);
                      setIsManualMode(false);
                      setMatchedTransactionIds([]);
                      setSelectedTransactionIds([]);
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-12 pr-4 text-right text-sm font-black text-slate-900 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-4 rounded-xl border border-slate-100 bg-slate-50/80 p-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <SafeIcon
                  icon={
                    summary.isBalanced
                    && endingBalance !== ''
                      ? FiCheckCircle
                      : FiInfo
                  }
                  className={`mt-0.5 ${
                    summary.isBalanced
                    && endingBalance !== ''
                      ? 'text-emerald-600'
                      : 'text-slate-400'
                  }`}
                />

                <div>
                  <p className="text-sm font-black text-slate-900">
                    Book balance:{' '}
                    {formatMoney(
                      summary.clearedBalance,
                      currency
                    )}
                  </p>

                  {endingBalance === '' ? (
                    <p className="mt-1 text-xs font-medium text-slate-500">
                      Enter your statement’s closing balance to verify the account.
                    </p>
                  ) : summary.isBalanced ? (
                    <p className="mt-1 text-xs font-bold text-emerald-700">
                      Balances match. You can choose automatic or manual reconciliation.
                    </p>
                  ) : (
                    <p className="mt-1 text-xs font-bold text-rose-600">
                      Difference:{' '}
                      {formatMoney(
                        summary.difference,
                        currency
                      )}
                      . Starting reconciliation opens manual matching so you can review the statement transactions.
                    </p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={!hasRequiredDetails || isSaving}
                className="inline-flex min-w-[220px] items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-200 disabled:shadow-none"
              >
                {isSaving && !isMatchModalOpen
                  ? 'Starting...'
                  : 'Start reconciling'}
              </button>
            </div>
          </form>
        </section>
      )}

      {error && isHistoryManual && (
        <div className="mt-4 flex gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-700">
          <SafeIcon
            icon={FiAlertCircle}
            className="mt-0.5 shrink-0"
          />

          {error}
        </div>
      )}

      {isManualMode && (
        <section className="mt-5 overflow-hidden rounded-2xl border border-blue-500 bg-white shadow-sm">
          <div className="grid gap-4 border-b border-blue-200 bg-blue-50 px-5 py-5 text-center sm:grid-cols-2 lg:grid-cols-5 lg:items-center">
            <div>
              <p className="text-xs font-bold text-slate-600">
                Unmatched transactions
              </p>

              <p className="mt-1 text-2xl font-black text-slate-950">
                {unmatchedCount}
              </p>
            </div>

            <div>
              <p className="text-xs font-bold text-slate-600">
                Closing balance
              </p>

              <p className="mt-1 text-2xl font-black text-slate-950">
                {formatMoney(
                  endingBalance,
                  currency
                )}
              </p>

              <p className="mt-1 text-xs font-medium text-slate-500">
                Reconciling up to {formatDate(statementDate)}
              </p>
            </div>

            <div className="hidden text-2xl font-black text-slate-950 lg:block">
              =
            </div>

            <div>
              <p className="text-xs font-bold text-slate-600">
                Matched balance
              </p>

              <p className="mt-1 text-2xl font-black text-slate-950">
                {formatMoney(
                  matchedBalance,
                  currency
                )}
              </p>
            </div>

            <div>
              <p className="text-xs font-bold text-slate-600">
                Difference
              </p>

              <p
                className={`mt-1 text-2xl font-black ${
                  isManualBalanced
                    ? 'text-emerald-600'
                    : 'text-amber-600'
                }`}
              >
                {formatMoney(
                  manualDifference,
                  currency
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 border-b border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-black text-slate-900">
                Reconcile transactions manually
              </h3>

              <p className="mt-1 text-sm font-medium text-slate-600">
                Select transactions, then match or delete them in bulk.
              </p>
            </div>

            <span
              className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-black ${
                isManualBalanced
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700'
              }`}
            >
              <SafeIcon
                icon={
                  isManualBalanced
                    ? FiCheckCircle
                    : FiAlertCircle
                }
              />

              {isManualBalanced
                ? 'Ready to reconcile'
                : 'Manual review required'}
            </span>
          </div>

          {selectedTransactionIds.length > 0 && (
            <div className="flex flex-col gap-3 border-b border-blue-100 bg-blue-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 text-xs font-black text-white">
                  {selectedTransactionIds.length}
                </span>

                <span className="text-sm font-black text-blue-950">
                  transaction
                  {selectedTransactionIds.length === 1
                    ? ''
                    : 's'} selected
                </span>
              </div>

              <div className="flex items-center gap-2">
                <ActionButton
                  label="Match selected transactions"
                  icon={FiGitMerge}
                  onClick={
                    handleMatchSelectedTransactions
                  }
                  className="border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                  disabled={isSaving}
                />

                <ActionButton
                  label="Delete selected transactions"
                  icon={FiTrash2}
                  onClick={
                    handleDeleteSelectedTransactions
                  }
                  className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                  disabled={isSaving}
                />

                <button
                  type="button"
                  onClick={() =>
                    setSelectedTransactionIds([])
                  }
                  disabled={isSaving}
                  className="rounded-lg px-3 py-2 text-xs font-black text-slate-600 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-left">
              <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="w-14 px-5 py-4">
                    <input
                      type="checkbox"
                      checked={
                        allManualTransactionsSelected
                      }
                      onChange={
                        handleSelectAllTransactions
                      }
                      aria-label="Select all reconciliation transactions"
                      className="h-5 w-5 cursor-pointer accent-blue-600"
                    />
                  </th>

                  <th className="px-6 py-4">
                    Date
                  </th>

                  <th className="px-6 py-4">
                    Description
                  </th>

                  <th className="px-6 py-4 text-right">
                    Deposit
                  </th>

                  <th className="px-6 py-4 text-right">
                    Withdrawal
                  </th>

                  <th className="px-6 py-4 text-right">
                    Balance
                  </th>

                  <th className="px-6 py-4 text-center">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {manualTransactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-12 text-center text-sm font-bold text-slate-400"
                    >
                      No unreconciled transactions exist for this statement period.
                    </td>
                  </tr>
                ) : (
                  manualTransactions.map(
                    (transaction) => {
                      const isDeposit =
                        transaction.amount >= 0;

                      const isMatched =
                        matchedTransactionIds.includes(
                          transaction.id
                        );

                      const isSelected =
                        selectedTransactionIds.includes(
                          transaction.id
                        );

                      return (
                        <tr
                          key={transaction.id}
                          className={`transition-colors ${
                            isMatched
                              ? 'bg-blue-50/80'
                              : isSelected
                                ? 'bg-blue-50/50'
                                : 'hover:bg-blue-50/30'
                          }`}
                        >
                          <td className="px-5 py-5">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                handleSelectTransaction(
                                  transaction.id
                                )
                              }
                              aria-label={`Select ${
                                transaction.description
                                || 'transaction'
                              }`}
                              className="h-5 w-5 cursor-pointer accent-blue-600"
                            />
                          </td>

                          <td className="whitespace-nowrap px-6 py-5 text-sm font-bold text-slate-600">
                            {formatDate(
                              transaction.date
                            )}
                          </td>

                          <td className="px-6 py-5 text-sm font-black text-slate-900">
                            {transaction.description
                              || 'No description'}
                          </td>

                          <td className="px-6 py-5 text-right text-sm font-black text-emerald-700">
                            {isDeposit
                              ? formatMoney(
                                transaction.amount,
                                currency
                              )
                              : '—'}
                          </td>

                          <td className="px-6 py-5 text-right text-sm font-black text-slate-900">
                            {!isDeposit
                              ? formatMoney(
                                transaction.amount,
                                currency
                              )
                              : '—'}
                          </td>

                          <td
                            className={`px-6 py-5 text-right text-sm font-black ${
                              transaction.runningBalance < 0
                                ? 'text-rose-600'
                                : 'text-slate-900'
                            }`}
                          >
                            {formatMoney(
                              transaction.runningBalance,
                              currency
                            )}
                          </td>

                          <td className="px-6 py-5">
                            <div className="flex items-center justify-center gap-2">
                              <ActionButton
                                label={
                                  isMatched
                                    ? 'Unmatch transaction'
                                    : 'Match transaction'
                                }
                                icon={
                                  isMatched
                                    ? FiCheckCircle
                                    : FiGitMerge
                                }
                                onClick={() =>
                                  handleMatchTransaction(
                                    transaction.id
                                  )
                                }
                                className={
                                  isMatched
                                    ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700'
                                    : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
                                }
                              />

                              <ActionButton
                                label="Edit transaction"
                                icon={FiEdit2}
                                onClick={() =>
                                  setEditingTransaction(
                                    transaction
                                  )
                                }
                                className="border-blue-200 bg-white text-blue-700 hover:bg-blue-50"
                              />

                              <ActionButton
                                label="Delete transaction"
                                icon={FiTrash2}
                                onClick={() =>
                                  setDeletingTransaction(
                                    transaction
                                  )
                                }
                                className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-slate-50 px-5 py-4">
            <p
              className={`text-sm font-bold ${
                isManualBalanced
                  ? 'text-emerald-700'
                  : 'text-amber-700'
              }`}
            >
              {isManualBalanced
                ? 'The selected transactions match the statement and are ready to reconcile.'
                : `Match transactions to resolve ${formatMoney(
                  Math.abs(manualDifference),
                  currency
                )}.`}
            </p>

            <button
              type="button"
              onClick={
                handleCompleteManualReconciliation
              }
              disabled={
                !isManualBalanced
                || isSaving
              }
              className="shrink-0 rounded-xl bg-blue-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-200 disabled:shadow-none"
            >
              {isSaving
                ? 'Reconciling...'
                : 'End reconciliation'}
            </button>
          </div>
        </section>
      )}

      {isMatchModalOpen && (
        <ReconciliationMatchModal
          accountName={selectedAccount.name}
          isReconciling={isSaving}
          onClose={() =>
            setIsMatchModalOpen(false)
          }
          onReconcileAutomatically={
            handleAutomaticReconciliation
          }
          onReconcileManually={
            handleManualReconciliation
          }
        />
      )}

      {completedAccount && (
        <ReconciliationSuccessModal
          accountName={completedAccount.name}
          onClose={() =>
            setCompletedAccount(null)
          }
          onViewHistory={onViewHistory}
        />
      )}

      <TransactionModal
        isOpen={Boolean(editingTransaction)}
        initialData={editingTransaction}
        defaultType={
          editingTransaction?.amount < 0
            ? 'Withdrawal'
            : 'Deposit'
        }
        onClose={() =>
          setEditingTransaction(null)
        }
        onSuccess={async () => {
          await onCompleted?.(selectedAccount);
          setEditingTransaction(null);
        }}
      />

      {deletingTransaction && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-black text-slate-900">
                  Delete{' '}
                  {Array.isArray(deletingTransaction)
                    ? 'transactions'
                    : 'transaction'}
                  ?
                </h3>

                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-500">
                  {Array.isArray(deletingTransaction)
                    ? `${deletingTransaction.length} selected transactions and their balancing ledger entries will be removed.`
                    : 'This removes the selected transaction and its balancing ledger entry.'}
                  {' '}
                  Your matched balance will update afterwards.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setDeletingTransaction(null)
                }
                className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close delete confirmation"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() =>
                  setDeletingTransaction(null)
                }
                disabled={isSaving}
                className="rounded-xl px-5 py-2.5 text-sm font-black text-slate-600 transition-colors hover:bg-slate-100"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleDeleteTransaction}
                disabled={isSaving}
                className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-black text-white transition-colors hover:bg-rose-700 disabled:opacity-50"
              >
                {isSaving
                  ? 'Deleting...'
                  : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}